import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";


// Fetch all pages with search & pagination
export const fetchAllPages = createAsyncThunk(
  "page/fetchAll",
  async ({ page = 1, limit = 10, search = "" } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/page/pages", {
        params: { page, limit, search },
      });
      return response.data; // { total, page, limit, pages }
    } catch (err) {
      return rejectWithValue(err.response?.data || { error: err.message });
    }
  }
);


// Fetch single page by slug
export const fetchPageBySlug = createAsyncThunk(
  "page/fetchBySlug",
  async (slug, { rejectWithValue }) => {
    try {
      const response = await api.get(`/page/${slug}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { error: err.message });
    }
  }
);

// Upsert page
export const upsertPageBySlug = createAsyncThunk(
  "page/upsert",
  async ({ slug, pageData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/page/${slug}`, pageData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { error: err.message });
    }
  }
);

const pageSlice = createSlice({
  name: "page",
  initialState: {
    pages: [],       // array of pages
    total: 0,        // total number of pages
    page: 1,         // current page
    limit: 10,       // items per page
    loading: false,
    error: null,
    success: false,
    pageData: null,  // single page
  },
  reducers: {
    clearPageState: (state) => {
      state.pages = [];
      state.total = 0;
      state.page = 1;
      state.limit = 10;
      state.loading = false;
      state.error = null;
      state.success = false;
      state.pageData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all pages
      .addCase(fetchAllPages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllPages.fulfilled, (state, action) => {
        state.loading = false;
        state.pages = action.payload.pages;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
      })
      .addCase(fetchAllPages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "Failed to fetch pages";
      })

      // Fetch single page
      .addCase(fetchPageBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPageBySlug.fulfilled, (state, action) => {
        state.loading = false;
        state.pageData = action.payload;
      })
      .addCase(fetchPageBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "Failed to fetch page";
      })

      // Upsert page
      .addCase(upsertPageBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(upsertPageBySlug.fulfilled, (state, action) => {
        state.loading = false;
        state.pageData = action.payload.page;
        state.success = true;
      })
      .addCase(upsertPageBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "Failed to upsert page";
        state.success = false;
      });
  },
});

export const { clearPageState } = pageSlice.actions;
export default pageSlice.reducer;
